import { useEffect, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";

/**
 * Auto-verify milestone rules. Each maps an `auto_verify_source` value
 * to a check that returns true when the user has performed the activity.
 */
type VerifyFn = (userId: string) => Promise<boolean>;

const verifiers: Record<string, VerifyFn> = {
  credit_readiness_results: async (userId) => {
    const { count } = await supabase
      .from("credit_readiness_results")
      .select("id", { count: "exact", head: true })
      .eq("user_id", userId);
    return (count ?? 0) > 0;
  },
  wellness_surveys: async (userId) => {
    const { count } = await supabase
      .from("wellness_surveys")
      .select("id", { count: "exact", head: true })
      .eq("user_id", userId);
    return (count ?? 0) > 0;
  },
  profile_complete: async (userId) => {
    const { data } = await supabase
      .from("profile_details")
      .select("finances_completed, goals_completed, preferences_completed")
      .eq("user_id", userId)
      .single();
    if (!data) return false;
    const d = data as any;
    return d.finances_completed && d.goals_completed && d.preferences_completed;
  },
  learning_path_progress: async (userId) => {
    const { count } = await supabase
      .from("learning_path_progress")
      .select("id", { count: "exact", head: true })
      .eq("user_id", userId);
    return (count ?? 0) > 0;
  },
  webinar_rsvps: async (userId) => {
    const { count } = await supabase
      .from("webinar_rsvps")
      .select("id", { count: "exact", head: true })
      .eq("user_id", userId);
    return (count ?? 0) > 0;
  },
  diy_projects: async (userId) => {
    const { count } = await supabase
      .from("diy_projects")
      .select("id", { count: "exact", head: true })
      .eq("user_id", userId);
    return (count ?? 0) > 0;
  },
  user_referrals: async (userId) => {
    const { count } = await supabase
      .from("user_referrals")
      .select("id", { count: "exact", head: true })
      .eq("referrer_id", userId)
      .eq("status", "completed");
    return (count ?? 0) > 0;
  },
};

/** Check tool:* prefixed sources against tool_completions table */
const checkToolCompletion = async (userId: string, toolSlug: string): Promise<boolean> => {
  const { count } = await supabase
    .from("tool_completions")
    .select("id", { count: "exact", head: true })
    .eq("user_id", userId)
    .eq("tool_slug", toolSlug);
  return (count ?? 0) > 0;
};

interface MilestoneRow {
  id: string;
  auto_verify_source: string | null;
  points_reward: number;
  title: string;
}

/**
 * Runs on mount. For each milestone with an `auto_verify_source`,
 * checks if the user has completed the activity and auto-inserts
 * a progress row if not already present.
 */
export function useAutoMilestones(
  trackTables: { milestones: string; progress: string },
  userId: string | undefined,
  completedIds: Set<string>,
  pendingIds: Set<string>,
  onAutoComplete?: (milestoneId: string, points: number) => void
) {
  const { user } = useAuth();

  const runAutoVerify = useCallback(async () => {
    if (!userId) return;

    // Fetch milestones that have auto_verify_source set
    const { data: allMilestones } = await supabase
      .from(trackTables.milestones as any)
      .select("id, auto_verify_source, points_reward, title")
      .not("auto_verify_source", "is", null);

    if (!allMilestones || allMilestones.length === 0) return;

    const milestones = allMilestones as unknown as MilestoneRow[];

    for (const m of milestones) {
      // Skip if already completed or pending
      if (completedIds.has(m.id) || pendingIds.has(m.id)) continue;
      if (!m.auto_verify_source) continue;

      let verified = false;

      if (m.auto_verify_source.startsWith("tool:")) {
        const toolSlug = m.auto_verify_source.replace("tool:", "");
        verified = await checkToolCompletion(userId, toolSlug);
      } else if (verifiers[m.auto_verify_source]) {
        verified = await verifiers[m.auto_verify_source](userId);
      }

      if (verified) {
        // Auto-insert progress row with pending status
        const { error } = await supabase.from(trackTables.progress as any).insert({
          user_id: userId,
          milestone_id: m.id,
          points_earned: m.points_reward,
          status: "pending",
        } as any);

        if (!error) {
          onAutoComplete?.(m.id, m.points_reward);
        }
      }
    }
  }, [userId, trackTables, completedIds, pendingIds, onAutoComplete]);

  useEffect(() => {
    if (userId) {
      runAutoVerify();
    }
  }, [userId, runAutoVerify]);

  return { runAutoVerify };
}

/**
 * Utility to save a tool completion to the database.
 * Call this from any calculator/tool after the user runs a calculation.
 */
export async function saveToolCompletion(
  userId: string,
  toolSlug: string,
  resultData: Record<string, any>
) {
  const { error } = await supabase.from("tool_completions").insert({
    user_id: userId,
    tool_slug: toolSlug,
    result_data: resultData,
  });
  return { error };
}
